import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Methode() {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => setShowContent(true), 100);

    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com';
    const scriptId = 'jsonld-methode';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'La méthode Usine à Leads en 5 piliers',
      description: 'Découvrez le système complet qui permet à nos clients de générer des leads qualifiés en continu, de manière prévisible et scalable.',
      url: `${siteUrl}/methode`,
      inLanguage: 'fr-FR',
      publisher: {
        '@type': 'Organization',
        name: 'Usine à Leads',
        url: siteUrl,
      },
      step: [
        { '@type': 'HowToStep', position: 1, name: 'Audit & Stratégie', text: 'Analyse de votre marché, vos concurrents et vos opportunités de croissance.' },
        { '@type': 'HowToStep', position: 2, name: 'Tunnel de conversion', text: 'Création de pages et parcours optimisés pour transformer les visiteurs en prospects.' },
        { '@type': 'HowToStep', position: 3, name: 'Acquisition ciblée', text: 'Campagnes publicitaires et SEO pour attirer un trafic qualifié en continu.' },
        { '@type': 'HowToStep', position: 4, name: 'Automatisation & CRM', text: 'Séquences de nurturing et relances automatiques pour ne perdre aucun lead.' },
        { '@type': 'HowToStep', position: 5, name: 'Optimisation continue', text: 'Analyse des données et itérations pour améliorer les performances chaque semaine.' },
      ],
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Accueil', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Notre Méthode', item: `${siteUrl}/methode` },
        ],
      },
    });
    document.head.appendChild(script);

    return () => {
      clearTimeout(timer);
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white">
      <title>Notre Méthode | Usine à Leads - 5 Piliers pour Générer des Leads Qualifiés</title>
      <meta name="description" content="Découvrez la méthode Usine à Leads en 5 piliers : audit stratégique, tunnel de conversion, acquisition ciblée, automatisation CRM et optimisation continue." />
      <meta name="keywords" content="méthode génération leads, tunnel de conversion, acquisition ciblée, automatisation marketing, stratégie leads qualifiés" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/methode`} />
      <meta property="og:title" content="Notre Méthode | Usine à Leads - 5 Piliers pour Générer des Leads Qualifiés" />
      <meta property="og:description" content="Découvrez le système complet qui permet à nos clients de générer des leads qualifiés en continu, de manière prévisible et scalable." />
      <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/methode`} />
      <meta property="og:type" content="website" />

      {/* Navbar */}
      <nav className="w-full py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            className="cursor-pointer"
          >
            <img
              src="https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/512cce5c00ccd36955d2d4e00fd25839.png"
              alt="Usine à Leads"
              className="h-10 w-auto"
            />
          </a>
        </div>
      </nav>

      {/* Contenu principal */}
      <main
        className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 transition-all duration-700 ${
          showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        {/* Header Méthode */}
        <section className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orange-500/20 mb-6">
            <i className="ri-rocket-2-fill text-4xl text-orange-400"></i>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 leading-tight">
            La méthode <span className="text-orange-400">Usine à Leads</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
            Découvrez le système complet qui permet à nos clients de générer des leads qualifiés en continu, de manière prévisible et scalable.
          </p>
        </section>

        {/* Section préparation */}
        <section className="mb-10 sm:mb-14">
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] rounded-2xl border border-white/10 p-6 sm:p-10">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-flash-line text-2xl text-orange-400"></i>
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-2">
                  Comment ça fonctionne&nbsp;?
                </h2>
                <p className="text-white/60 leading-relaxed">
                  Notre méthode repose sur 5 piliers éprouvés qui, combinés, créent un système d&apos;acquisition autonome et performant.
                </p>
              </div>
            </div>

            {/* Les 5 piliers */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white/90 flex items-center gap-2">
                <i className="ri-stack-line text-orange-400"></i>
                Les 5 piliers de la méthode
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-white/70">
                  <i className="ri-checkbox-circle-line text-emerald-400 mt-0.5 flex-shrink-0"></i>
                  <span><strong className="text-white">Audit &amp; Stratégie</strong> — Analyse de votre marché, vos concurrents et vos opportunités de croissance</span>
                </li>
                <li className="flex items-start gap-3 text-white/70">
                  <i className="ri-checkbox-circle-line text-emerald-400 mt-0.5 flex-shrink-0"></i>
                  <span><strong className="text-white">Tunnel de conversion</strong> — Création de pages et parcours optimisés pour transformer les visiteurs en prospects</span>
                </li>
                <li className="flex items-start gap-3 text-white/70">
                  <i className="ri-checkbox-circle-line text-emerald-400 mt-0.5 flex-shrink-0"></i>
                  <span><strong className="text-white">Acquisition ciblée</strong> — Campagnes publicitaires et SEO pour attirer un trafic qualifié en continu</span>
                </li>
                <li className="flex items-start gap-3 text-white/70">
                  <i className="ri-checkbox-circle-line text-emerald-400 mt-0.5 flex-shrink-0"></i>
                  <span><strong className="text-white">Automatisation &amp; CRM</strong> — Séquences de nurturing et relances automatiques pour ne perdre aucun lead</span>
                </li>
                <li className="flex items-start gap-3 text-white/70">
                  <i className="ri-checkbox-circle-line text-emerald-400 mt-0.5 flex-shrink-0"></i>
                  <span><strong className="text-white">Optimisation continue</strong> — Analyse des données et itérations pour améliorer les performances chaque semaine</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Video Placeholder */}
        <section className="mb-10 sm:mb-14">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              La méthode en vidéo
            </h2>
            <p className="text-white/60 max-w-xl mx-auto">
              Regardez cette présentation pour comprendre comment nous construisons votre machine à leads étape par étape.
            </p>
          </div>
          <div
            className="relative w-full rounded-2xl overflow-hidden shadow-2xl shadow-orange-500/10 border border-white/10 bg-gray-800"
            style={{ aspectRatio: '16/9' }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <div className="w-20 h-20 rounded-full bg-orange-500/20 flex items-center justify-center cursor-pointer hover:bg-orange-500/30 transition-all duration-300 group">
                <i className="ri-play-fill text-4xl text-orange-400 group-hover:text-orange-300 ml-1"></i>
              </div>
              <p className="text-white/50 text-sm font-medium">Vidéo à venir</p>
            </div>
          </div>
        </section>

        {/* Ce que vous allez découvrir */}
        <section className="mb-16 sm:mb-20">
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-orange-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center mb-4">
                <i className="ri-target-line text-2xl text-orange-400"></i>
              </div>
              <h3 className="font-bold text-lg mb-2">Stratégie sur-mesure</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Nous analyserons ensemble votre marché et définirons une stratégie adaptée à vos objectifs.
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-emerald-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-2xl text-emerald-400"></i>
              </div>
              <h3 className="font-bold text-lg mb-2">Projections réalistes</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Vous repartirez avec des estimations concrètes de résultats basées sur votre secteur.
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-teal-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-teal-500/20 flex items-center justify-center mb-4">
                <i className="ri-roadmap-line text-2xl text-teal-400"></i>
              </div>
              <h3 className="font-bold text-lg mb-2">Plan d&apos;action clair</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Un roadmap détaillé pour lancer votre machine à leads en quelques semaines.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center mb-10">
          <button
            data-iclosed-link="https://app.iclosed.io/e/usinealead/rdv-45-min-votre-business-votre-besoin"
            data-embed-type="popup"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-line text-lg"></i>
            Réserver mon appel stratégique
          </button>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-4 text-center text-xs text-white/40">
          © {new Date().getFullYear()} Usine à Leads. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
}
